/**
 * Bookings Page - Modern CRM Redesign
 * Clean, intuitive booking management with unified status system
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Plus,
  RefreshCw,
  Calendar,
  Car,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  Check,
  X,
  Eye,
} from 'lucide-react';
import { Button, StatusBadge } from '../components/ui';
import { useBookingList, useConfirmBooking } from '../hooks/useBookings';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { CancellationModal } from '../components/booking/CancellationModal';
import { MiniCalendarSidebar, getInitialDateRange } from '../components/booking/MiniCalendarSidebar';
import type { DateRange } from '../components/booking/MiniCalendarSidebar';
import { cn } from '../lib/utils';
import { pageStyles, tableStyles, formStyles, buttonStyles } from '../lib/styles';
import { ApiError } from '../services/api';
import type { Booking, BookingSearchParams } from '../services/bookingsApi';
import type { UnifiedCaseType } from '@leal-crm/shared';
import { useToast } from '../contexts/ToastContext';

/**
 * Map caseCategory to booking type for workflow lookup
 */
function getBookingType(caseCategory?: string): UnifiedCaseType {
  switch (caseCategory) {
    case 'seguro':
      return 'insurance';
    case 'garantia':
      return 'warranty';
    default:
      return 'private';
  }
}

// Unified status groups for filtering
const STATUS_GROUPS = {
  all: {
    label: 'Todas',
    statuses: [],
    color: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    activeColor: 'bg-gray-900 text-white',
  },
  unverified: {
    label: 'Sin Verificar',
    statuses: ['UNVERIFIED'],
    color: 'bg-rose-50 text-rose-700 hover:bg-rose-100',
    activeColor: 'bg-rose-600 text-white',
  },
  preArrival: {
    label: 'Pre-llegada',
    statuses: ['PENDING_CONFIRMATION', 'CONFIRMED', 'AWAITING_DROPOFF'],
    color: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    activeColor: 'bg-blue-600 text-white',
  },
  inWorkshop: {
    label: 'En Taller',
    statuses: ['DROPOFF_COMPLETE'],
    color: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
    activeColor: 'bg-purple-600 text-white',
  },
  completed: {
    label: 'Completadas',
    statuses: ['DROPOFF_COMPLETE'],
    color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    activeColor: 'bg-emerald-600 text-white',
  },
  cancelled: {
    label: 'Canceladas',
    statuses: ['CANCELLED', 'NO_SHOW'],
    color: 'bg-red-50 text-red-700 hover:bg-red-100',
    activeColor: 'bg-red-600 text-white',
  },
} as const;

type StatusGroup = keyof typeof STATUS_GROUPS;

const ITEMS_PER_PAGE = 25;

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  if (isToday) {
    return `Hoy, ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (isTomorrow) {
    return `Manana, ${date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  }

  return date.toLocaleDateString('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get source display label
 */
function getSourceLabel(source?: string): string {
  switch (source) {
    case 'staff':
      return 'Staff';
    case 'public_widget':
      return 'Widget';
    case 'marketing_link':
      return 'Marketing';
    case 'customer':
      return 'Cliente';
    default:
      return 'Staff';
  }
}

/**
 * Get source badge color classes
 */
function getSourceBadgeClass(source?: string): string {
  switch (source) {
    case 'public_widget':
      return 'bg-blue-50 text-blue-700';
    case 'marketing_link':
      return 'bg-purple-50 text-purple-700';
    case 'customer':
      return 'bg-green-50 text-green-700';
    case 'staff':
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Booking Table Row
 */
function BookingRow({
  booking,
  onConfirm,
  onCancel
}: {
  booking: Booking;
  onConfirm: (booking: Booking) => void;
  onCancel: (booking: Booking) => void;
}) {
  const status = booking.status || booking.workflowStage || 'CONFIRMED';
  const isTerminal = ['COMPLETED', 'CANCELLED', 'NO_SHOW', 'completado', 'cancelado', 'no_show'].includes(status);
  const needsConfirmation = !booking.confirmed && ['PENDING_CONFIRMATION', 'CONFIRMED'].includes(status);
  const isUnverified = status === 'UNVERIFIED';
  // Check if verification has expired (>24h since creation)
  const isVerificationExpired = isUnverified && booking.createdAt
    && (Date.now() / 1000 - booking.createdAt) > 24 * 60 * 60;

  return (
    <tr className={cn(tableStyles.row, 'group')}>
      {/* Reference & Type */}
      <td className={cn(tableStyles.cell, 'w-28')}>
        <Link
          to={booking.caseId ? `/cases/${booking.caseId}` : `/bookings/${booking.id}`}
          className="font-mono text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          {booking.bookingReference || booking.id.slice(0, 8)}
        </Link>
        <div className="text-[10px] text-gray-400 mt-0.5">
          {booking.caseCategory === 'seguro' ? 'Seguro' : booking.caseCategory === 'garantia' ? 'Garantia' : 'Particular'}
        </div>
      </td>

      {/* Status */}
      <td className={tableStyles.cell}>
        <div className="flex items-center gap-1.5">
          <StatusBadge status={status} bookingType={getBookingType(booking.caseCategory)} />
          {isVerificationExpired && (
            <span title="Verificación expirada (+24h)" className="text-red-500">
              <AlertTriangle className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </td>

      {/* Vehicle (navigable) */}
      <td className={tableStyles.cell}>
        <Link
          to={`/vehicles/${booking.vehicleId}`}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          {booking.vehicle?.registration?.toUpperCase() || '-'}
        </Link>
        <div className="text-[10px] text-gray-500 truncate">
          {booking.vehicle ? `${booking.vehicle.make} ${booking.vehicle.model}` : '-'}
        </div>
      </td>

      {/* Customer (navigable) */}
      <td className={tableStyles.cell}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <Link
              to={`/customers/${booking.customerId}`}
              className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline truncate block"
            >
              {booking.customer?.name || '-'}
            </Link>
            <div className="text-[10px] text-gray-500 truncate">
              {booking.customer?.phone || booking.customer?.email || '-'}
            </div>
          </div>
        </div>
      </td>

      {/* Date */}
      <td className={cn(tableStyles.cell, 'whitespace-nowrap')}>
        <div className="flex items-center gap-1.5 text-xs text-gray-700">
          <Clock className="w-3 h-3 text-gray-400" />
          {formatDateShort(booking.scheduledAt)}
        </div>
      </td>

      {/* Source */}
      <td className={tableStyles.cell}>
        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', getSourceBadgeClass(booking.source))}>
          {getSourceLabel(booking.source)}
        </span>
      </td>

      {/* Created By */}
      <td className={tableStyles.cell}>
        {booking.createdBy ? (
          <Link
            to={`/users/${booking.createdBy.id}`}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            {booking.createdBy.name}
          </Link>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>

      {/* Created At */}
      <td className={cn(tableStyles.cell, 'whitespace-nowrap')}>
        <span className="text-[10px] text-gray-500">
          {formatDateShort(booking.createdAt)}
        </span>
      </td>

      {/* Actions */}
      <td className={cn(tableStyles.cell, 'text-right')}>
        <div className="flex items-center justify-end gap-1">
          {needsConfirmation && (
            <button
              onClick={(e) => { e.stopPropagation(); onConfirm(booking); }}
              className="p-1 rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
              title="Confirmar"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {!isTerminal && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(booking); }}
              className="p-1 rounded text-red-600 hover:bg-red-50 transition-colors"
              title="Cancelar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <Link
            to={booking.caseId ? `/cases/${booking.caseId}` : `/bookings/${booking.id}`}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            title="Ver detalle"
          >
            <Eye className="w-3.5 h-3.5" />
          </Link>
        </div>
      </td>
    </tr>
  );
}

/**
 * Booking Card (for grid view)
 */
function BookingCard({
  booking,
  onConfirm,
  onCancel
}: {
  booking: Booking;
  onConfirm: (booking: Booking) => void;
  onCancel: (booking: Booking) => void;
}) {
  const status = booking.status || booking.workflowStage || 'CONFIRMED';
  const isTerminal = ['COMPLETED', 'CANCELLED', 'NO_SHOW', 'completado', 'cancelado', 'no_show'].includes(status);
  const needsConfirmation = !booking.confirmed && ['PENDING_CONFIRMATION', 'CONFIRMED'].includes(status);
  const isUnverified = status === 'UNVERIFIED';
  // Check if verification has expired (>24h since creation)
  const isVerificationExpired = isUnverified && booking.createdAt
    && (Date.now() / 1000 - booking.createdAt) > 24 * 60 * 60;

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group">
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={booking.caseId ? `/cases/${booking.caseId}` : `/bookings/${booking.id}`}
            className="font-mono text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            {booking.bookingReference || booking.id.slice(0, 8)}
          </Link>
          <div className="flex items-center gap-1.5">
            <StatusBadge status={status} bookingType={getBookingType(booking.caseCategory)} />
            {isVerificationExpired && (
              <span title="Verificación expirada (+24h)" className="text-red-500">
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2.5">
        {/* Customer */}
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <User className="w-3 h-3 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-gray-900 truncate">
              {booking.customer?.name || '-'}
            </div>
          </div>
        </div>

        {/* Vehicle */}
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
            <Car className="w-3 h-3 text-gray-600" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs font-medium text-gray-700">
              {booking.vehicle?.registration?.toUpperCase() || '-'}
            </span>
            <span className="text-[10px] text-gray-400 ml-1.5">
              {booking.vehicle?.make} {booking.vehicle?.model}
            </span>
          </div>
        </div>

        {/* Date */}
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          {formatDate(booking.scheduledAt)}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-50 bg-gray-50/50 rounded-b-lg flex items-center justify-between">
        <span className="text-[10px] text-gray-500">
          {booking.caseCategory === 'seguro' ? 'Seguro' : booking.caseCategory === 'garantia' ? 'Garantia' : 'Particular'}
        </span>
        <div className="flex items-center gap-1">
          {needsConfirmation && (
            <button
              onClick={() => onConfirm(booking)}
              className="p-1 rounded text-emerald-600 hover:bg-emerald-100 transition-colors"
              title="Confirmar"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {!isTerminal && (
            <button
              onClick={() => onCancel(booking)}
              className="p-1 rounded text-red-600 hover:bg-red-100 transition-colors"
              title="Cancelar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <Link
            to={booking.caseId ? `/cases/${booking.caseId}` : `/bookings/${booking.id}`}
            className="text-[10px] font-medium text-blue-600 hover:text-blue-800 px-1.5"
          >
            Ver
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Empty State
 */
function EmptyState({ hasFilters, onClearFilters }: {
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Calendar className="w-6 h-6 text-gray-400" />
      </div>
      <h3 className="text-sm font-medium text-gray-900 mb-1">
        {hasFilters ? 'Sin resultados' : 'Sin citas'}
      </h3>
      <p className="text-xs text-gray-500 mb-4 max-w-xs">
        {hasFilters
          ? 'No se encontraron citas con los filtros aplicados'
          : 'No hay citas registradas. Crea una nueva cita para empezar.'}
      </p>
      {hasFilters ? (
        <Button variant="secondary" size="sm" onClick={onClearFilters}>
          Limpiar filtros
        </Button>
      ) : (
        <Button size="sm" asChild>
          <Link to="/bookings/new">
            <Plus className="w-3.5 h-3.5 mr-1" />
            Nueva Cita
          </Link>
        </Button>
      )}
    </div>
  );
}

/**
 * Loading Skeleton
 */
function TableSkeleton() {
  return (
    <div className="animate-pulse">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100">
          <div className="w-20 h-4 bg-gray-100 rounded" />
          <div className="w-32 h-4 bg-gray-100 rounded" />
          <div className="w-24 h-4 bg-gray-100 rounded" />
          <div className="w-28 h-4 bg-gray-100 rounded" />
          <div className="w-16 h-5 bg-gray-100 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/**
 * Main Bookings Page
 */
export function BookingsPage() {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusGroup, setStatusGroup] = useState<StatusGroup>('all');
  const [specificStatus, setSpecificStatus] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange);
  const [currentPage, setCurrentPage] = useState(0);
  const viewMode = 'table' as const; // Cards view disabled for now
  const [cancelModalBooking, setCancelModalBooking] = useState<Booking | null>(null);
  const { error: showError } = useToast();

  // Debounced search
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Mutations
  const confirmMutation = useConfirmBooking();

  // Build search params
  const searchParams: BookingSearchParams = useMemo(() => {
    const params: BookingSearchParams = {
      limit: ITEMS_PER_PAGE,
      offset: currentPage * ITEMS_PER_PAGE,
    };

    // Add date filter from calendar
    if (dateRange.type !== 'all' && dateRange.startDate && dateRange.endDate) {
      const startOfDay = new Date(dateRange.startDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(dateRange.endDate);
      endOfDay.setHours(23, 59, 59, 999);
      params.startDate = Math.floor(startOfDay.getTime() / 1000);
      params.endDate = Math.floor(endOfDay.getTime() / 1000);
    }

    // Add status filter - specific status takes precedence
    if (specificStatus) {
      params.stage = specificStatus;
    } else if (statusGroup !== 'all') {
      // For group filters, we'll filter client-side after fetching
      // since the API accepts single status, not arrays
    }

    return params;
  }, [currentPage, dateRange, specificStatus, statusGroup]);

  // Fetch bookings
  const { data, isLoading, isError, error, refetch } = useBookingList(searchParams);

  const formatErrorMessage = (err: unknown) => {
    if (err instanceof ApiError) {
      const statusPart = err.status ? ` (${err.status})` : '';
      const detailsPart = err.details ? ` - ${JSON.stringify(err.details)}` : '';
      return `${err.message} [${err.code}]${statusPart}${detailsPart}`;
    }
    return err instanceof Error ? err.message : 'Error desconocido';
  };

  useEffect(() => {
    if (isError) {
      showError(formatErrorMessage(error));
    }
  }, [error, isError, showError]);

  // Filter and search bookings
  const filteredBookings = useMemo(() => {
    let bookings = data?.items || [];

    // Filter by status group (client-side)
    if (statusGroup !== 'all' && !specificStatus) {
      const groupStatuses = STATUS_GROUPS[statusGroup].statuses;
      bookings = bookings.filter((b: Booking) => {
        const status = b.status || b.workflowStage || '';
        return groupStatuses.some(s =>
          status.toUpperCase() === s ||
          status.toLowerCase() === s.toLowerCase()
        );
      });
    }

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      bookings = bookings.filter((b: Booking) => {
        const searchFields = [
          b.customer?.name,
          b.customer?.phone,
          b.customer?.email,
          b.vehicle?.registration,
          b.vehicle?.make,
          b.vehicle?.model,
          b.bookingReference,
        ].filter(Boolean).map(s => s?.toLowerCase());

        return searchFields.some(field => field?.includes(query));
      });
    }

    return bookings;
  }, [data?.items, statusGroup, specificStatus, debouncedSearch]);

  // Pagination
  const totalItems = data?.total || filteredBookings.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Has active filters
  const hasFilters = statusGroup !== 'all' || !!specificStatus || dateRange.type !== 'all' || !!debouncedSearch;

  // Handlers
  const handleClearFilters = () => {
    setStatusGroup('all');
    setSpecificStatus('');
    // Reset to 'all' and clear localStorage
    const allRange = {
      type: 'all' as const,
      startDate: null,
      endDate: null,
      label: 'Todas las fechas',
    };
    setDateRange(allRange);
    try {
      localStorage.setItem('bookings-date-filter', 'all');
    } catch {
      // Ignore
    }
    setSearchQuery('');
    setCurrentPage(0);
  };

  const handleStatusGroupChange = (group: StatusGroup) => {
    setStatusGroup(group);
    setSpecificStatus('');
    setCurrentPage(0);
  };

  const handleConfirm = async (booking: Booking) => {
    try {
      await confirmMutation.mutateAsync(booking.id);
      refetch();
    } catch (err) {
      showError(formatErrorMessage(err));
    }
  };

  // Stats for each group
  const stats = useMemo(() => {
    const items = data?.items || [];
    return {
      all: items.length,
      unverified: items.filter((b: Booking) =>
        STATUS_GROUPS.unverified.statuses.some(s =>
          (b.status || b.workflowStage || '').toUpperCase() === s
        )
      ).length,
      preArrival: items.filter((b: Booking) =>
        STATUS_GROUPS.preArrival.statuses.some(s =>
          (b.status || b.workflowStage || '').toUpperCase() === s
        )
      ).length,
      inWorkshop: items.filter((b: Booking) =>
        STATUS_GROUPS.inWorkshop.statuses.some(s =>
          (b.status || b.workflowStage || '').toUpperCase() === s
        )
      ).length,
      completed: items.filter((b: Booking) =>
        STATUS_GROUPS.completed.statuses.some(s =>
          (b.status || b.workflowStage || '').toUpperCase() === s
        )
      ).length,
      cancelled: items.filter((b: Booking) =>
        STATUS_GROUPS.cancelled.statuses.some(s =>
          (b.status || b.workflowStage || '').toUpperCase() === s
        )
      ).length,
    };
  }, [data?.items]);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className={pageStyles.title}>Citas</h1>
            <p className={pageStyles.subtitle}>Gestiona las citas del taller</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className={formStyles.searchIcon} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar..."
                className={cn(formStyles.inputWithIcon, 'w-48')}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className={buttonStyles.icon}
              title="Actualizar"
            >
              <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
            </button>

            {/* New Booking */}
            <Button size="sm" asChild>
              <Link to="/bookings/new">
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Nueva Cita</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Status Group Tabs */}
        <div className="mt-3 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {(Object.keys(STATUS_GROUPS) as StatusGroup[]).map((group) => {
            const config = STATUS_GROUPS[group];
            const count = stats[group];
            const isActive = statusGroup === group;

            return (
              <button
                key={group}
                onClick={() => handleStatusGroupChange(group)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap',
                  isActive ? config.activeColor : config.color
                )}
              >
                {config.label}
                {count > 0 && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                    isActive ? 'bg-white/20' : 'bg-gray-900/5'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

      </div>

      {/* Content with Sidebar */}
      <div className="flex-1 overflow-auto p-4 scrollbar-hide">
        <div className="flex gap-4">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
        {isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-red-800 mb-1">Error al cargar</h3>
            <p className="text-xs text-red-600 mb-3">{error?.message}</p>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Reintentar
            </Button>
          </div>
        ) : isLoading ? (
          <div className={tableStyles.container}>
            <TableSkeleton />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className={tableStyles.container}>
            <EmptyState
              hasFilters={hasFilters}
              onClearFilters={handleClearFilters}
            />
          </div>
        ) : viewMode === 'table' ? (
          <div className={tableStyles.container}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={tableStyles.headerRow}>
                  <tr>
                    <th className={tableStyles.headerCell}>Ref</th>
                    <th className={tableStyles.headerCell}>Estado</th>
                    <th className={tableStyles.headerCell}>Vehiculo</th>
                    <th className={tableStyles.headerCell}>Cliente</th>
                    <th className={tableStyles.headerCell}>Fecha Cita</th>
                    <th className={tableStyles.headerCell}>Origen</th>
                    <th className={tableStyles.headerCell}>Creado por</th>
                    <th className={tableStyles.headerCell}>Creado</th>
                    <th className={cn(tableStyles.headerCell, 'text-right w-24')}>Acciones</th>
                  </tr>
                </thead>
                <tbody className={tableStyles.body}>
                  {filteredBookings.map((booking: Booking) => (
                    <BookingRow
                      key={booking.id}
                      booking={booking}
                      onConfirm={handleConfirm}
                      onCancel={setCancelModalBooking}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">
                  {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalItems)} de {totalItems}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-600 px-2">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredBookings.map((booking: Booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onConfirm={handleConfirm}
                onCancel={setCancelModalBooking}
              />
            ))}
          </div>
        )}
          </div>

          {/* Right Sidebar - Calendar */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-4">
              <MiniCalendarSidebar
                dateRange={dateRange}
                onDateRangeChange={(range) => {
                  setDateRange(range);
                  setCurrentPage(0);
                }}
              />

              {/* Quick Stats */}
              {hasFilters && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="text-[10px] text-gray-500 mb-2">Filtros activos</div>
                  <button
                    onClick={handleClearFilters}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Limpiar todos
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {cancelModalBooking && (
        <CancellationModal
          open={!!cancelModalBooking}
          onOpenChange={(open) => !open && setCancelModalBooking(null)}
          bookingId={cancelModalBooking.id}
          bookingReference={cancelModalBooking.bookingReference}
          customerName={cancelModalBooking.customer?.name}
          scheduledAt={cancelModalBooking.scheduledAt * 1000}
          onSuccess={() => {
            setCancelModalBooking(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}
